/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import {
    conditionalString,
    nullToUndefined,
    sortAsc,
    sortDateDesc,
} from '@strata/helper-functions';
import { type GridWithFilters } from '@strata/react-grid';
import { type ClaimStatusIcon } from '@strata/react-status-icons';
import { type ClaimStatusIndicator } from '@strata/react-status-indicators';
import { type ClaimMilestone } from '@strata/react-timeline';
import { type ComponentProps, useCallback } from 'react';

import { MED_BILLING_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import {
    type ClaimFrequencyType,
    type ClaimPlaceOfServiceMap,
    type ClaimUomMap,
    type SubmissionType,
} from 'services/MedicalBillingConstants';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const TagType = { CLAIMS: 'CLAIMS', CLAIM_DETAILS: 'CLAIM_DETAILS' } as const;

export interface ClaimSummary {
    transaction_id: string;
    resubmitted_transaction_id?: string;
    supplier_npi: string;
    payer_id: string;
    claim_id: string;
    referral_id: string;
    submitted_date: string;
    product_service_display_name: string;
    winningbid_rate?: string;
    claim_frequency_type: ClaimFrequencyType;
    status_999: (typeof ClaimStatusIcon.Statuses)[keyof typeof ClaimStatusIcon.Statuses];
    status_277_ca: (typeof ClaimStatusIcon.Statuses)[keyof typeof ClaimStatusIcon.Statuses];
    internal_claim_status: (typeof ClaimStatusIcon.Statuses)[keyof typeof ClaimStatusIcon.Statuses];
    notes_count: number;
    start_date_of_service: string;
    end_date_of_service: string;
    snooze_date?: string;
    snooze_reason?: string;
    snooze_follow_up_date?: string;
}

export interface ClaimDetails {
    claimId: string;
    referralId?: string;
    transactionId: string;
    resubmittedTransactionId?: string;
    caseId?: string;
    submittedDate: string;
    originalClaimNumber?: string;
    payerClaimNumber?: string;
    submissionType: SubmissionType;
    transactionExpiration?: string;
    supplierNpi: string;
    taxonomyCode: string;
    placeOfService: keyof typeof ClaimPlaceOfServiceMap;
    patientAccountNumber: string;
    claimFrequencyType: ClaimFrequencyType;
    providerSignatureOnFile: string;
    providerAcceptAssignment: string;
    assignmentOfBenefits: string;
    releaseOfInformation: string;
    diagnoses: string[];
    submittedAmount?: number;
    contractualObligation: number;
    payerAmount?: number;
    otherAdj: number;
    memberLiability?: number;
    adjTotal: number;
    claimTotal: number;
    checkNumber?: string;
    memberId: string;
    memberFirstName: string;
    memberLastName: string;
    memberAddress1: string;
    memberAddress2?: string;
    memberCity: string;
    memberState: string;
    memberZip: string;
    memberGender: string;
    memberDOB: string;
    status999: string;
    status277CA: string;
    claimStatus: string;
    snoozeDate?: string;
    snoozeReason?: string;
    snoozeFollowUpDate?: string;
    claimCategoryCode?: string;
    claimStatusCode?: string;
    secondaryClaimCategoryCode?: string;
    secondaryClaimStatusCode?: string;
    prescriberLastName?: string;
    prescriberFirstName?: string;
    prescriberNpi?: string;
    prescriberAddress1?: string;
    prescriberAddress2?: string;
    prescriberCity?: string;
    prescriberState?: string;
    prescriberZip?: string;
    services: {
        payerClaimNumber?: string;
        dateOfService: string;
        dateOfServiceEnd: string;
        procedureCode: string;
        procedureCodeDescription: string;
        procedureCodeDisplayText: string;
        modifierCodes: string;
        ndc?: string;
        diagnoses: string[];
        dispensedQty?: number;
        dispensedQtyUom?: keyof typeof ClaimUomMap;
        billingQty: number;
        billingQtyUom: keyof typeof ClaimUomMap;
        providerCharge: number;
        submittedAmount: number;
        contractualObligation: number;
        payerAmount: number;
        otherAdj: number;
        memberLiability: number;
        adjTotal: number;
        total?: number;
        adjCodes: string[];
        serviceStatus?: (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
        serviceStatusCode?: string;
        serviceCategoryCode?: string;
        serviceStatusCodes: string[];
        secondaryServiceStatusCode?: string;
        secondaryServiceCategoryCode?: string;
        secondaryServiceStatusCodes: string[];
        entityIdentifierCode?: string;
        fmhFlag: boolean;
    }[];
    adjCodes: {
        code: string;
        description: string;
        code2: string;
        description2: string;
    }[];
}

export interface RawTransactions {
    raw999?: string[];
    raw277CA?: string[];
    rawInternalClaim?: string[];
}

export interface ClaimHistoryRecord {
    submittedDate: string;
    transactionId: string;
    comment?: string;
    username?: string;
}

const ediSplitRegex = /(?<=~)/g;

export const formatCodeDescLabel = <T extends string | undefined>(
    code: T,
    desc: string | undefined,
    descFirst = false,
): T extends string ? string : undefined => {
    if (!code) {
        return undefined as T extends string ? string : undefined;
    }

    if (descFirst) {
        const descStr = conditionalString(!!desc, `${desc} - `);
        return `${descStr}${code}` as T extends string ? string : undefined;
    }

    const descStr = conditionalString(!!desc, ` - ${desc}`);
    return `${code}${descStr}` as T extends string ? string : undefined;
};

const formatStatusCodes = ({
    categoryCode,
    categoryCodeDesc,
    statusCode,
    statusCodeDesc,
}: {
    categoryCode?: string;
    categoryCodeDesc?: string;
    statusCode?: string;
    statusCodeDesc?: string;
}) => [
    ...(categoryCode ? [formatCodeDescLabel(categoryCode, categoryCodeDesc)] : []),
    ...(statusCode ? [formatCodeDescLabel(statusCode, statusCodeDesc)] : []),
];

const strToNumber = <T extends string | undefined | null>(val: T) =>
    val != null ? Number(val) : (undefined as T extends string ? number : undefined);

export const medbillingApi = createApi({
    baseQuery: getBaseQuery(MED_BILLING_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'medbillingApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getClaims: build.query<
            {
                claims: ClaimSummary[];
                count: number;
                filters: Record<string, string[]>;
            },
            {
                page: number;
                searchText?: string;
                activeFilters?: ComponentProps<typeof GridWithFilters>['activeFilters'];
                sort?: ComponentProps<typeof GridWithFilters>['sortProperties'];
            }
        >({
            query: ({ page, searchText, activeFilters, sort }) => ({
                url: 'admin/transactions',
                method: 'POST',
                body: {
                    page,
                    search_text: searchText,
                    submitted_date: activeFilters?.submitted_date?.[0],
                    research_status_filter: activeFilters?.snooze_date?.[0],
                    filters: activeFilters,
                    sort_id: sort?.[0].id,
                    sort_dir: sort?.[0].desc ? 'desc' : 'asc',
                },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving claims.'),
            providesTags: [TagType.CLAIMS],
        }),
        getClaimDetails: build.query<ClaimDetails, { payerId: string; transactionId: string }>({
            query: ({ payerId, transactionId }) => ({
                url: `admin/${payerId}/transactions/${transactionId}`,
                params: { include_estimate_adjustments: true },
            }),
            // eslint-disable-next-line complexity
            transformResponse: (response: {
                claim_id: string;
                referral_id?: string;
                transaction_id: string;
                resubmitted_transaction_id?: string;
                case_id?: string;
                submitted_date: string;
                original_claim_number?: string;
                payer_claim_number?: string;
                submission_type: SubmissionType;
                transaction_expiration?: string;
                supplier_npi: string;
                taxonomy_code: string;
                place_of_service: keyof typeof ClaimPlaceOfServiceMap;
                patient_account_number: string;
                claim_frequency_type: ClaimFrequencyType;
                provider_signature_on_file: string;
                provider_accept_assignment: string;
                assignment_of_benefits: string;
                release_of_information: string;
                diagnoses: string[];
                submitted_amount?: string;
                contractual_obligation: string;
                payer_amount?: string;
                other_adjustments: string;
                member_liability?: string;
                adjustment_total: string;
                claim_total: string;
                check_number?: string;
                member_id: string;
                member_first_name: string;
                member_last_name: string;
                member_address_1: string;
                member_address_2?: string;
                member_city: string;
                member_state: string;
                member_zip: string;
                member_gender: string;
                member_dob: string;
                status_999: string;
                status_277_ca: string;
                internal_claim_status: string;
                snooze_date?: string;
                snooze_reason?: string;
                snooze_follow_up_date?: string;
                claim_category_code?: string;
                claim_status_code?: string;
                secondary_claim_category_code?: string;
                secondary_claim_status_code?: string;
                services: {
                    payer_claim_number?: string;
                    date_of_service: string;
                    date_of_service_end: string;
                    procedure_code: string;
                    procedure_code_description: string;
                    modifier_codes: string[];
                    ndc?: string;
                    diagnoses: string[];
                    dispensed_quantity?: string;
                    dispensed_uom?: keyof typeof ClaimUomMap;
                    billing_quantity: string;
                    billing_uom: keyof typeof ClaimUomMap;
                    provider_charge: string;
                    submitted_amount: string;
                    contractual_obligation: string;
                    payer_amount: string;
                    other_adjustments: string;
                    member_liability: string;
                    adjustment_total: string;
                    total?: string;
                    service_status?: (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
                    service_status_code?: string;
                    service_status_code_desc?: string;
                    service_category_code?: string;
                    service_category_code_desc?: string;
                    secondary_service_status_code?: string;
                    secondary_service_status_code_desc?: string;
                    secondary_service_category_code?: string;
                    secondary_service_category_code_desc?: string;
                    entity_identifier_code?: string;
                    prescriber_last_name?: string;
                    prescriber_first_name?: string;
                    prescriber_npi?: string;
                    prescriber_address_1?: string;
                    prescriber_address_2?: string;
                    prescriber_city?: string;
                    prescriber_state?: string;
                    prescriber_zip?: string;
                    fmh_flag: boolean;
                    adjustments: {
                        adjustment_group_code: string;
                        adjustment_group_code_desc: string;
                        adjustment_reason_code: string;
                        adjustment_reason_code_desc: string;
                    }[];
                }[];
                adjustments: {
                    adjustment_group_code: string;
                    adjustment_group_code_desc: string;
                    adjustment_reason_code: string;
                    adjustment_reason_code_desc: string;
                }[];
            }) => ({
                claimId: response?.claim_id,
                referralId: response?.referral_id,
                transactionId: response?.transaction_id,
                resubmittedTransactionId: response?.resubmitted_transaction_id,
                caseId: response?.case_id,
                submittedDate: response?.submitted_date,
                originalClaimNumber: nullToUndefined(response?.original_claim_number),
                payerClaimNumber: nullToUndefined(response?.payer_claim_number),
                submissionType: response?.submission_type,
                transactionExpiration: response?.transaction_expiration,
                supplierNpi: response?.supplier_npi,
                taxonomyCode: response?.taxonomy_code,
                placeOfService: response?.place_of_service,
                patientAccountNumber: response?.patient_account_number,
                claimFrequencyType: response?.claim_frequency_type,
                providerSignatureOnFile: response?.provider_signature_on_file,
                providerAcceptAssignment: response?.provider_accept_assignment,
                assignmentOfBenefits: response?.assignment_of_benefits,
                releaseOfInformation: response?.release_of_information,
                diagnoses: response?.diagnoses,
                submittedAmount: strToNumber(response?.submitted_amount),
                contractualObligation: strToNumber(response?.contractual_obligation),
                payerAmount: strToNumber(response?.payer_amount),
                otherAdj: strToNumber(response?.other_adjustments),
                memberLiability: strToNumber(response?.member_liability),
                adjTotal: strToNumber(response?.adjustment_total),
                claimTotal: strToNumber(response?.claim_total),
                memberId: response?.member_id,
                memberFirstName: response?.member_first_name,
                memberLastName: response?.member_last_name,
                memberDOB: response?.member_dob,
                memberGender: response?.member_gender,
                memberAddress1: response?.member_address_1,
                memberAddress2: nullToUndefined(response?.member_address_2),
                memberCity: response?.member_city,
                memberState: response?.member_state,
                memberZip: response?.member_zip,
                // Prescriber details are the same across services
                prescriberLastName: nullToUndefined(response?.services?.[0]?.prescriber_last_name),
                prescriberFirstName: nullToUndefined(
                    response?.services?.[0]?.prescriber_first_name,
                ),
                prescriberNpi: nullToUndefined(response?.services?.[0]?.prescriber_npi),
                prescriberAddress1: nullToUndefined(response?.services?.[0]?.prescriber_address_1),
                prescriberAddress2: nullToUndefined(response?.services?.[0]?.prescriber_address_2),
                prescriberCity: nullToUndefined(response?.services?.[0]?.prescriber_city),
                prescriberState: nullToUndefined(response?.services?.[0]?.prescriber_state),
                prescriberZip: nullToUndefined(response?.services?.[0]?.prescriber_zip),
                status999: response?.status_999,
                status277CA: response?.status_277_ca,
                claimStatus: response?.internal_claim_status,
                snoozeDate: response?.snooze_date,
                snoozeReason: response?.snooze_reason,
                snoozeFollowUpDate: response?.snooze_follow_up_date,
                claimCategoryCode: nullToUndefined(response?.claim_category_code),
                claimStatusCode: nullToUndefined(response?.claim_status_code),
                checkNumber: nullToUndefined(response?.check_number),
                secondaryClaimCategoryCode: nullToUndefined(
                    response?.secondary_claim_category_code,
                ),
                secondaryClaimStatusCode: nullToUndefined(response?.secondary_claim_status_code),
                services: response?.services?.map((service) => ({
                    payerClaimNumber: nullToUndefined(service.payer_claim_number),
                    dateOfService: service.date_of_service,
                    dateOfServiceEnd: service.date_of_service_end,
                    procedureCode: service.procedure_code,
                    procedureCodeDescription: service.procedure_code_description,
                    procedureCodeDisplayText: formatCodeDescLabel(
                        service.procedure_code,
                        service.procedure_code_description,
                        true,
                    ),
                    modifierCodes: service.modifier_codes.join(','),
                    ndc: nullToUndefined(service.ndc),
                    diagnoses: service.diagnoses,
                    dispensedQty: strToNumber(service.dispensed_quantity),
                    dispensedQtyUom: nullToUndefined(service.dispensed_uom),
                    billingQty: strToNumber(service.billing_quantity),
                    billingQtyUom: service.billing_uom,
                    providerCharge: strToNumber(service.provider_charge),
                    submittedAmount: strToNumber(service.submitted_amount),
                    contractualObligation: strToNumber(service.contractual_obligation),
                    payerAmount: strToNumber(service.payer_amount),
                    otherAdj: strToNumber(service.other_adjustments),
                    memberLiability: strToNumber(service.member_liability),
                    adjTotal: strToNumber(service.adjustment_total),
                    total: strToNumber(service.total),
                    adjCodes: service.adjustments
                        ?.map((code) =>
                            [code.adjustment_group_code, code.adjustment_reason_code].join(' - '),
                        )
                        .sort(sortAsc),
                    serviceStatus: service.service_status,
                    fmhFlag: service.fmh_flag,
                    serviceCategoryCode: nullToUndefined(service.service_category_code),
                    serviceStatusCode: nullToUndefined(service.service_status_code),
                    entityIdentifierCode: nullToUndefined(service.entity_identifier_code),
                    secondaryServiceCategoryCode: nullToUndefined(
                        service.secondary_service_category_code,
                    ),
                    secondaryServiceStatusCode: nullToUndefined(
                        service.secondary_service_status_code,
                    ),
                    serviceStatusCodes: formatStatusCodes({
                        categoryCode: service.service_category_code,
                        categoryCodeDesc: service.service_category_code_desc,
                        statusCode: service.service_status_code,
                        statusCodeDesc: service.service_status_code_desc,
                    }),
                    secondaryServiceStatusCodes: formatStatusCodes({
                        categoryCode: service.secondary_service_category_code,
                        categoryCodeDesc: service.secondary_service_category_code_desc,
                        statusCode: service.secondary_service_status_code,
                        statusCodeDesc: service.secondary_service_status_code_desc,
                    }),
                })),
                adjCodes: response?.adjustments
                    // Filter out duplicate codes
                    ?.filter(
                        (code, index, arr) =>
                            arr.findIndex(
                                (c) =>
                                    code.adjustment_group_code === c.adjustment_group_code &&
                                    code.adjustment_group_code_desc ===
                                        c.adjustment_group_code_desc &&
                                    code.adjustment_reason_code === c.adjustment_reason_code &&
                                    code.adjustment_reason_code_desc ===
                                        c.adjustment_reason_code_desc,
                            ) === index,
                    )
                    .map((code) => ({
                        code: code.adjustment_group_code,
                        description: code.adjustment_group_code_desc,
                        code2: code.adjustment_reason_code,
                        description2: code.adjustment_reason_code_desc,
                    })),
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving claim details.',
            ),
            providesTags: (_result, _error, { transactionId }) => [
                { type: TagType.CLAIM_DETAILS, id: transactionId },
            ],
        }),
        getClaimMilestones: build.query<
            ClaimMilestone[],
            { payerId: string; transactionId: string }
        >({
            query: ({ payerId, transactionId }) =>
                `admin/${payerId}/transactions/${transactionId}/milestones`,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving claim timeline.',
            ),
        }),
        getClaimRawTransactions: build.query<
            RawTransactions,
            { payerId: string; transactionId: string }
        >({
            query: ({ payerId, transactionId }) =>
                `admin/${payerId}/transactions/${transactionId}/raw`,
            transformResponse: (response: {
                raw_999?: string;
                raw_277_ca?: string;
                raw_internal_claim?: string;
            }) => ({
                raw999: response?.raw_999?.split(ediSplitRegex),
                raw277CA: response?.raw_277_ca?.split(ediSplitRegex),
                rawInternalClaim: response?.raw_internal_claim?.split(ediSplitRegex),
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving raw transactions.',
            ),
        }),
        getClaimHistory: build.query<
            ClaimHistoryRecord[],
            { payerId: string; transactionId: string }
        >({
            query: ({ payerId, transactionId }) =>
                `admin/${payerId}/transactions/${transactionId}/history`,
            transformResponse: (
                response: {
                    submitted_date: string;
                    transaction_id: string;
                    comment?: string;
                    username?: string;
                }[],
            ) =>
                response
                    ?.map((claim) => ({
                        submittedDate: claim.submitted_date,
                        transactionId: claim.transaction_id,
                        comment: claim.comment,
                        username: claim.username,
                    }))
                    .sort((a, b) => sortDateDesc(a.submittedDate, b.submittedDate)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving claim history.',
            ),
        }),
        getStatusCodes: build.query<string[], { payerId: string }>({
            query: ({ payerId }) => `admin/${payerId}/codes/status`,
            transformResponse: (response: { code: string; description: string }[]) =>
                response?.map((code) => code.code),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving status codes.',
            ),
        }),
        getCategoryCodes: build.query<string[], { payerId: string }>({
            query: ({ payerId }) => `admin/${payerId}/codes/category`,
            transformResponse: (response: { code: string; description: string }[]) =>
                response?.map((code) => code.code),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving category codes.',
            ),
        }),
        getNotesForClaim: build.query<
            {
                transactionId: string;
                conversationId: string;
                createdByUpn: string;
                createdByFirstName: string;
                createdByLastName: string;
                createdTime: string;
                details: { subject: string };
                isUnread: boolean;
            }[],
            { payerId: string; transactionId: string; searchText?: string }
        >({
            query: ({ payerId, transactionId, searchText }) => ({
                url: `admin/${payerId}/notes/claim/${transactionId}`,
                params: { search_text: searchText },
            }),
            transformResponse: (
                response: {
                    conversation_id: string;
                    transaction_id: string;
                    created_time: string;
                    created_by_upn: string;
                    details: { subject: string };
                    created_by_first_name: string;
                    created_by_last_name: string;
                }[],
            ) =>
                response?.map((note) => ({
                    transactionId: note.transaction_id,
                    conversationId: note.conversation_id,
                    createdByUpn: note.created_by_upn,
                    createdByFirstName: note.created_by_first_name,
                    createdByLastName: note.created_by_last_name,
                    createdTime: note.created_time,
                    details: note.details,
                    isUnread: false,
                })),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving notes.'),
        }),
        getConversationById: build.query<
            {
                conversationId: string;
                transactionId: string;
                createdTime: string;
                createdByUpn: string;
                details: { subject: string };
                createdByFirstName: string;
                createdByLastName: string;
                messages: {
                    messageId: string;
                    conversationId: string;
                    upn: string;
                    sentTime: string;
                    senderFirstName: string;
                    senderLastName: string;
                    content: { message: string };
                }[];
            },
            { payerId: string; conversationId: string }
        >({
            query: ({ payerId, conversationId }) => `admin/${payerId}/notes/${conversationId}`,
            transformResponse: (response: {
                conversation_id: string;
                transaction_id: string;
                created_time: string;
                created_by_upn: string;
                details: { subject: string };
                created_by_first_name: string;
                created_by_last_name: string;
                messages: {
                    message_id: string;
                    conversation_id: string;
                    upn: string;
                    sent_time: string;
                    first_name: string;
                    last_name: string;
                    content: { message: string };
                }[];
            }) => ({
                conversationId: response?.conversation_id,
                transactionId: response?.transaction_id,
                createdByUpn: response?.created_by_upn,
                createdByFirstName: response?.created_by_first_name,
                createdByLastName: response?.created_by_last_name,
                createdTime: response?.created_time,
                details: response?.details,
                messages: response?.messages?.map((message) => ({
                    content: message.content,
                    conversationId: message.conversation_id,
                    senderFirstName: message.first_name,
                    senderLastName: message.last_name,
                    messageId: message.message_id,
                    sentTime: message.sent_time,
                    upn: message.upn,
                })),
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving messages.'),
        }),
        putSnoozeClaim: build.mutation<
            void,
            { payerId: string; transactionId: string; reason: string; snoozeFollowUpDate: string }
        >({
            query: ({ payerId, transactionId, reason, snoozeFollowUpDate }) => ({
                url: `admin/${payerId}/actions/transactions/${transactionId}/snooze`,
                method: 'PUT',
                body: { snooze_reason: reason, snooze_follow_up_date: snoozeFollowUpDate },
            }),
            transformErrorResponse: handleRTKQueryError('Unable to snooze. Please try again.'),
            invalidatesTags: (_, error, { transactionId }) =>
                !error ? [TagType.CLAIMS, { type: TagType.CLAIM_DETAILS, id: transactionId }] : [],
        }),
        postUnsnoozeClaim: build.mutation<void, { payerId: string; transactionId: string }>({
            query: ({ payerId, transactionId }) => ({
                url: `admin/${payerId}/actions/transactions/${transactionId}/unsnooze`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError('Unable to unsnooze. Please try again.'),
            invalidatesTags: (_, error, { transactionId }) =>
                !error ? [TagType.CLAIMS, { type: TagType.CLAIM_DETAILS, id: transactionId }] : [],
        }),
    }),
});

export const {
    useGetClaimsQuery,
    useGetClaimDetailsQuery,
    useGetClaimMilestonesQuery,
    useGetClaimRawTransactionsQuery,
    useGetClaimHistoryQuery,
    useGetStatusCodesQuery,
    useGetCategoryCodesQuery,
    useGetNotesForClaimQuery,
    useGetConversationByIdQuery,
    usePutSnoozeClaimMutation,
    usePostUnsnoozeClaimMutation,
} = medbillingApi;

export const useRefetchAllClaimDetails = ({
    payerId,
    transactionId,
}: {
    payerId: string;
    transactionId: string;
}) => {
    const { refetch: refetchClaimDetails } = useGetClaimDetailsQuery({
        payerId,
        transactionId,
    });

    const { refetch: refetchClaimMilestones } = useGetClaimMilestonesQuery({
        payerId,
        transactionId,
    });

    const refetchAllData = useCallback(() => {
        (async () => {
            await refetchClaimDetails();
            await refetchClaimMilestones();
        })();
    }, [refetchClaimDetails, refetchClaimMilestones]);
    return refetchAllData;
};
